/**
 * Modulo per la gestione della pagina "Le Tue Vendite" area venditore
 * Gestisce l'eliminazione di annunci e l'invio di richieste di contatto per supporto
 */

import Page from "~/pages/Page";
import { getApi } from "~/auth";
import UI from "~/ui";
import $ from "jquery";
import { generaRecaptcha, recaptchaReady, recaptchaToken } from "~/recaptcha";

// Import Leaflet per le mappe
import * as L from "leaflet";

/** Interfaccia per la risposta della chiamata API */
interface ApiResponse {
    success: boolean;
    responseText?: string;
    message?: string;
}


/**
 * Classe per la gestione della pagina Le Tue Vendite
 */
class LeTueVendite extends Page {

    private auth: any = null;
    private userInfo: any = null;
    private isUserAuthenticated: boolean = false;
    private isSubmitting: boolean = false;
    private recaptchaGenerated: boolean = false;
    private currentReportId: string = "";
    private currentReportData: any = null;

    constructor() {
        super("ituoireport", "ITuoiReport");
    }

    /**
     * Inizializza la pagina
     */
    pageStart(): Promise<void> {
        this.auth = getApi();
        this.userInfo = this.auth.getCached("userInfo");
        this.isUserAuthenticated = this.userInfo !== null && this.userInfo !== undefined && this.userInfo.anagraficaId > 0;

        // Inizializza i gestori eventi
        this.initDeleteHandlers();
        this.initContactStaffHandlers();
        this.initModalHandlers();
        this.initReportHandlers(); // Aggiunto gestore per pulsante report
        this.initMapHandlers();
        this.initRenewReportHandlers()

        return Promise.resolve();
    }

    /**
     * Inizializza i gestori per il rinnovo dei report
     */
    private initRenewReportHandlers(): void {
        $("a.renew-report").on("click", (e) => {
            e.preventDefault();
            const reportId = $(e.currentTarget).attr("data-reportid");

            if (reportId) {
                UI.confirm(
                    "Conferma Rinnovo",
                    "Sei sicuro di voler rinnovare questo report?",
                    undefined,
                    async (result: boolean) => {
                        if (result) {
                            await this.rinnovaReport(reportId);
                        }
                    }
                );
            }
        });
    }

    /**
     * Rinnova un report esistente
     */
    private async rinnovaReport(reportId: string): Promise<void> {
        this.spinner.spin();

        try {
            // Crea un form data per l'invio POST (come elaboraReport)
            const formData = new FormData();
            formData.append('IdValutazioneRen', reportId);

            const url = `${window.BaseUrlWeb}Analisi/Refresha`;

            const response = await fetch(url, {
                method: 'POST',
                body: formData,
                credentials: 'include'
            });

            // Verifica se la risposta è un redirect
            if (response.redirected) {
                // Segui il redirect (alla pagina Results)
                window.location.href = response.url;
                return;
            }

            // Se non è un redirect, gestisci come JSON
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                const data: ApiResponse = await response.json();

                if (data.success) {
                    // Redirect alla pagina Results
                    window.location.href = `${window.BaseUrlWeb}Analisi/Results`;
                } else {
                    const errorMessage = data.responseText || data.message || "Errore durante il rinnovo del report";
                    UI.confirmOneButton("", `Errore: ${errorMessage}`);
                }
            } else {
                // Se la risposta è HTML, probabilmente è già la pagina Results
                window.location.href = `${window.BaseUrlWeb}Analisi/Results`;
            }

        } catch (error: any) {
            console.error("Errore durante il rinnovo del report:", error);
            UI.confirmOneButton("", `Errore durante il rinnovo del report: ${error.message || 'Errore sconosciuto'}`);
        } finally {
            this.spinner.stop();
        }
    }

    /**
     * Inizializza i gestori per l'eliminazione degli annunci
     */
    private initDeleteHandlers(): void {
        $("a.delete-report").on('click', (e: JQuery.ClickEvent) => {
            e.preventDefault();
            const reportId = $(e.currentTarget).attr("data-reportid");

            if (reportId) {
                UI.confirm(
                    "Conferma Eliminazione",
                    "Sei sicuro di voler eliminare questo report?",
                    undefined,
                    (result: boolean) => {
                        if (result) {
                            this.EliminaReport(reportId);
                        }
                    }
                );
            }
        });
    }

    /**
     * Elimina un annuncio/pratica
     */
    private async EliminaReport(reportId: string): Promise<void> {

        this.spinner.spin();

        try {

            const url = `${window.BaseUrlWebApi}VenditePrivate/DeleteReport?id=${reportId}`;
            await this.auth.fetchWithAuth("DELETE " + url, {
                cache: "no-cache",
                headers: { 'content-type': 'application/json' },
            });

            UI.confirmOneButton(
                "",
                "Report eliminato con successo",
                undefined,
                () => {
                    $(`#row-${reportId}`).remove();
                }
            );

        } catch (error: any) {
            console.error("Errore durante l'eliminazione del report:", error);
            UI.confirmOneButton("", "Errore durante l'eliminazione del report");
        } finally {
            this.spinner.stop();
        }
    }

    /**
     * Inizializza i gestori per il contatto con lo staff
     */
    private initContactStaffHandlers(): void {
        // Genera il token reCAPTCHA al click sul pulsante contatta staff
        $(".btn-vendi-con-noi").each((index, element) => {
            $(element).on("click", async (e: JQuery.ClickEvent) => {
                e.preventDefault();

                await this.generateRecaptcha();

                const reportId = $(e.currentTarget).attr("data-reportid");
                if (reportId) {
                    this.currentReportId = reportId;
                    const $row = $(`div[data-valid='${reportId}']`);
                    this.populateModalWithData($row);
                    $('#modal-contatto-i-tuoi-report').modal('show');
                }
            });
        });
    }

    /**
     * Genera il reCAPTCHA
     */
    private async generateRecaptcha(): Promise<void> {
        try {
            await recaptchaReady();
            await generaRecaptcha("report");
            this.recaptchaGenerated = true;
            console.info('reCAPTCHA generato per il form contatto i tuoi report');
        } catch (error) {
            console.error('Errore durante la generazione del reCAPTCHA:', error);
            this.recaptchaGenerated = false;
        }
    }

    /**
     * Popola il modale con i dati dell'annuncio selezionato
     */
    private populateModalWithData($row: JQuery): void {
        if (this.isUserAuthenticated && this.userInfo) {
            // Precompila i dati dell'utente
            $("#nome-report").val(this.userInfo.nome || "");
            $("#cognome-report").val(this.userInfo.cognome || "");
            $("#email-report").val(this.userInfo.email || "");

            // ✅ Recupera il telefono o cellulare dall'array contatti
            const telefono = this.getTelefonoFromUserInfo();
            if (telefono) {
                $("#telefono-report").val(telefono);
            }

            $("#hdReportId").val(this.currentReportId);
            // Popola i dati della pratica
            //const reportId = $row.find("#reportId").val();
            const indirizzo = $row.find("#indirizzo").text();
            const tipologia = $row.find("#tipologia").text().replace(/\s/g, '');
            const comune = $row.find("#citta").text();
            const superficie = $row.find("#superficie").text();

            //$("#hdReportId").val(reportId?.toString() || "");
            $("#tipologiabene-report").val(tipologia);
            $("#indirizzo-report").val(indirizzo);
            $("#citta-report").val(comune);
            $("#superficie-report").val(superficie);

            // Salva i dati per riferimento
            this.currentReportData = {
                reportId: this.currentReportId,
                indirizzo: indirizzo,
                tipologia: tipologia,
                comune: comune,
                superficie: superficie
            };
        }
    }

    /**
     * ✅ Recupera il numero di telefono o cellulare dall'array contatti dell'utente
     * Priorità: Cellulare (CEL) > Telefono (TEL)
     */
    private getTelefonoFromUserInfo(): string | null {
        if (!this.userInfo || !this.userInfo.contatti || !Array.isArray(this.userInfo.contatti)) {
            return null;
        }

        // Cerca prima un cellulare (CEL)
        const cellulare = this.userInfo.contatti.find((c: any) =>
            c.tipoContatto === 'CELL' && c.valore
        );

        if (cellulare) {
            return this.formatTelefono(cellulare);
        }

        // Se non c'è cellulare, cerca un telefono (TEL)
        const telefono = this.userInfo.contatti.find((c: any) =>
            c.tipoContatto === 'TEL' && c.valore
        );

        if (telefono) {
            return this.formatTelefono(telefono);
        }

        return null;
    }

    /**
     * ✅ Formatta il numero di telefono con eventuale prefisso
     */
    private formatTelefono(contatto: any): string {
        if (!contatto || !contatto.valore) {
            return '';
        }

        // Se c'è un prefisso, lo concatena
        if (contatto.prefisso && contatto.prefisso.trim() !== '') {
            return `${contatto.prefisso} ${contatto.valore}`;
        }

        return contatto.valore;
    }

    /**
     * Inizializza i gestori del modale
     */
    private initModalHandlers(): void {
        const theForm = document.getElementById('form-contatto-report') as HTMLFormElement;

        if (!theForm) {
            console.warn("Form contatto vendita non trovato");
            return;
        }

        // Gestione submit del form
        $("#invia-report").on("click", async (e: JQuery.ClickEvent) => {
            e.preventDefault();
            e.stopPropagation();

            // Impedisce il doppio click
            if (this.isSubmitting) {
                console.log("Invio già in corso...");
                return;
            }

            if (theForm.checkValidity()) {
                await this.richiestaContattoReport(theForm);
            } else {
                theForm.classList.add('was-validated');
            }
        });

        // Reset del form quando si chiude il modale
        $('#modal-contatto-i-tuoi-report').on('hidden.bs.modal', () => {
            theForm.reset();
            theForm.classList.remove('was-validated');
            $(".invalid-feedback").hide();
            this.recaptchaGenerated = false;
        });
    }

    /**
     * Invia la richiesta di contatto per l'annuncio
     */
    private async richiestaContattoReport(theForm: HTMLFormElement): Promise<void> {
        // Verifica il reCAPTCHA prima di procedere
        if (!this.recaptchaGenerated) {
            UI.confirmOneButton("", "Errore: reCAPTCHA non generato");
            return;
        }

        // Ottieni il token dal campo hidden
        const recaptchaTokenValue = recaptchaToken();

        if (!recaptchaTokenValue) {
            UI.confirmOneButton("", "Completa la verifica reCAPTCHA");
            return;
        }

        // Imposta il flag per impedire doppi click
        this.isSubmitting = true;
        this.disableSubmitButton(true);

        // Serializza i dati del form
        const formData: Record<string, any> = {};
        $(theForm)
            .serializeArray()
            .forEach((field) => {
                formData[field.name] = field.value;
            });

        // Aggiungi il token reCAPTCHA
        formData["RecaptchaToken"] = recaptchaTokenValue;

        console.info("Invio richiesta contatto annuncio:", formData);

        this.spinner.spin();

        try {
            const url = `${window.BaseUrlWeb}Venditore/RichiestaContattoReportMDI`;

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json; charset=utf-8'
                },
                /* credentials: 'include',*/
                body: JSON.stringify(formData)
            });

            const data: ApiResponse = await response.json();

            if (data.success) {
                // Mostra messaggio di successo
                UI.confirmOneButton(
                    "Messaggio inviato",
                    "La tua richiesta è stata inviata con successo. Verrai contattato al più presto."
                );

                // Reset del form e chiusura modale
                theForm.reset();
                theForm.classList.remove('was-validated');
                $('#modal-contatto-i-tuoi-report').modal('hide');

                // Rigenera il reCAPTCHA per il prossimo utilizzo
                this.recaptchaGenerated = false;
            }
            else {
                const errorMessage = data.responseText || data.message || "Errore sconosciuto";
                UI.confirmOneButton("", `Errore: Non è stato possibile inviare la E-mail (${errorMessage})`);

                // Rigenera il reCAPTCHA in caso di errore
                await this.generateRecaptcha();
            }

        } catch (error: any) {
            console.error("Errore durante l'invio della richiesta:", error);
            const errorMessage = error.message || "Si è verificato un errore durante l'invio";
            UI.confirmOneButton("", `Errore durante l'invio della richiesta: ${errorMessage}`);

            // Rigenera il reCAPTCHA in caso di errore
            await this.generateRecaptcha();
        } finally {
            this.spinner.stop();
            this.isSubmitting = false;
            this.disableSubmitButton(false);
        }
    }

    /**
     * Disabilita/abilita il pulsante di submit
     */
    private disableSubmitButton(disable: boolean): void {
        const $submitButton = $("#invia-report");

        if (disable) {
            $submitButton.prop('disabled', true);
            // Aggiunge uno spinner al pulsante
            const originalText = $submitButton.html();
            $submitButton.data('original-text', originalText);
            $submitButton.html('<span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Invio in corso...');
        } else {
            $submitButton.prop('disabled', false);
            // Ripristina il testo originale
            const originalText = $submitButton.data('original-text');
            if (originalText) {
                $submitButton.html(originalText);
            }
        }
    }


    /**
     * Inizializza i gestori per il pulsante Report
     */
    private initReportHandlers(): void {
        $(".report, .btn-report").on('click', async (e: JQuery.ClickEvent) => {
            e.preventDefault();

            const rawId = $(e.currentTarget).attr("data-reportid")
                || $(e.currentTarget).closest('.valutazione').attr('data-valid');

            if (!rawId) {
                console.error("ID Valutazione non trovato");
                UI.confirmOneButton("", "Errore: impossibile identificare il report");
                return;
            }

            // Converte la stringa in numero
            const valutazioneId = parseInt(rawId, 10);

            if (isNaN(valutazioneId)) {
                console.error("ID Valutazione non valido:", rawId);
                UI.confirmOneButton("", "Errore: ID report non valido");
                return;
            }

            await this.elaboraReport(valutazioneId);
        });
    }

    /**
     * Chiama il metodo Elabora del controller Analisi
     */
    private async elaboraReport(valutazioneId: number): Promise<void> {
        this.spinner.spin();

        try {
            // Crea un form data per l'invio POST
            const formData = new FormData();
            formData.append("idValutazione", valutazioneId.toString()); // ← toString() per FormData

            const url = window.BaseUrlWeb + "Analisi/Elabora";

            const response = await fetch(url, {
                method: 'POST',
                body: formData,
                credentials: 'include'
            });

            // Verifica se la risposta è un redirect
            if (response.redirected) {
                // Segui il redirect
                window.location.href = response.url;
                return;
            }

            // Se non è un redirect, gestisci come JSON
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                const data: ApiResponse = await response.json();

                if (data.success) {
                    // Redirect alla pagina Results
                    window.location.href = `${window.BaseUrlWeb}Analisi/Results`;
                } else {
                    const errorMessage = data.responseText || data.message || "Errore durante l'elaborazione del report";
                    UI.confirmOneButton("", `Errore: ${errorMessage}`);
                }
            } else {
                // Se la risposta è HTML, probabilmente è già la pagina Results
                window.location.href = `${window.BaseUrlWeb}Analisi/Results`;
            }

        } catch (error: any) {
            console.error("Errore durante l'elaborazione del report:", error);
            UI.confirmOneButton("", `Errore durante l'elaborazione del report: ${error.message || 'Errore sconosciuto'}`);
        } finally {
            this.spinner.stop();
        }
    }

    /**
     * Inizializza le mappe Leaflet per ogni valutazione
     */
    private initMapHandlers(): void {
        const that = this;

        // Costruzione della mappa per ogni valutazione
        $(".valutazione").each(function (index, item) {
            // Recupera l'ID della mappa dall'elemento con classe .map
            const mapId = $(item).find(".map").attr("id");

            if (!mapId) {
                console.warn("MapId non trovato per l'elemento", item);
                return;
            }

            // Recupera latitudine e longitudine dai campi hidden
            const latValue = $(item).find(`#Latitudine-${mapId}`).val() as string;
            const lngValue = $(item).find(`#Longitudine-${mapId}`).val() as string;

            if (!latValue || !lngValue) {
                console.warn(`Coordinate non trovate per mappa ${mapId}`);
                return;
            }

            // Converti le coordinate da stringa a numero (sostituendo virgola con punto)
            const lat = parseFloat(latValue.replace(",", "."));
            const lng = parseFloat(lngValue.replace(",", "."));

            if (isNaN(lat) || isNaN(lng)) {
                console.warn(`Coordinate non valide per mappa ${mapId}: lat=${latValue}, lng=${lngValue}`);
                return;
            }

            // Crea la mappa usando le opzioni specifiche per le valutazioni
            try {
                const map = L.map(mapId, {
                    center: [lat, lng],
                    zoom: 17,
                    zoomControl: false,
                    scrollWheelZoom: false,
                    dragging: false
                });

                // Aggiungi il tile layer
                L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
                    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                }).addTo(map);

                // Aggiungi il marker
                L.marker([lat, lng]).addTo(map);

                console.info(`Mappa ${mapId} inizializzata con successo: [${lat}, ${lng}]`);
            } catch (error) {
                console.error(`Errore durante l'inizializzazione della mappa ${mapId}:`, error);
            }
        });

    }


}

export default LeTueVendite;
